/**
 * JourneyPanel — Reusable collapsible panel for embedding journey components
 * into existing sidebar pages. Provides consistent styling and expand/collapse.
 */
import { useState } from "react";
import { ChevronDown, ChevronRight, Zap } from "lucide-react";

export function JourneyPanel({ title, ucId, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className="border border-[#1a2744] rounded-xl overflow-hidden mb-3 transition-all"
      data-testid={`journey-panel-${ucId}`}
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-[#0c1220] hover:bg-[#111c30] transition-colors text-left"
        data-testid={`journey-toggle-${ucId}`}
      >
        <Zap size={14} className="text-teal-400 shrink-0" />
        <span className="flex-1 text-sm font-semibold text-[#e2e8f0]">{title}</span>
        <span className="text-[10px] font-mono text-[#6b7a96] bg-[#111c30] px-2 py-0.5 rounded">{ucId}</span>
        {open ? <ChevronDown size={14} className="text-[#6b7a96]" /> : <ChevronRight size={14} className="text-[#6b7a96]" />}
      </button>
      {open && (
        <div className="px-4 py-4 bg-[#050a14] border-t border-[#1a2744]">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * JourneySection — Groups multiple JourneyPanels under a section header.
 * Drop this into any existing page to add live user journey functionality.
 */
export function JourneySection({ title, children }) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div className="mt-8" data-testid="journey-section">
      <div
        className="flex items-center gap-3 mb-4 cursor-pointer group"
        onClick={() => setCollapsed(c => !c)}
      >
        <div className="h-px flex-1 bg-gradient-to-r from-teal-500/40 to-transparent" />
        <div className="flex items-center gap-2 px-3 py-1.5 bg-teal-500/10 border border-teal-500/20 rounded-full">
          <Zap size={12} className="text-teal-400" />
          <span className="text-xs font-semibold text-teal-300 tracking-wide uppercase">{title}</span>
          {collapsed
            ? <ChevronRight size={12} className="text-teal-400" />
            : <ChevronDown size={12} className="text-teal-400" />}
        </div>
        <div className="h-px flex-1 bg-gradient-to-l from-teal-500/40 to-transparent" />
      </div>
      {!collapsed && children}
    </div>
  );
}
